import { useEffect, useMemo, useState } from "react";

import type {
	StaffingUnlock,
	UpdateStaffingUnlocksPayload,
} from "~/modules/billing/api/processedInvoice/processedInvoiceTypes.ts";

function getInitialSelection(staffingUnlocks: StaffingUnlock[]) {
	return new Set(staffingUnlocks.filter(staffingUnlock => staffingUnlock.unlocked).map(staffingUnlock => staffingUnlock.staffingId));
}

/** The requests that turn the current unlocks into the selection: only the staffings that differ. */
function getPayloads(staffingUnlocks: StaffingUnlock[], selection: Set<string>): UpdateStaffingUnlocksPayload[] {
	const toUnlock = staffingUnlocks
		.filter(staffingUnlock => selection.has(staffingUnlock.staffingId) && !staffingUnlock.unlocked)
		.map(staffingUnlock => staffingUnlock.staffingId);
	const toLock = staffingUnlocks
		.filter(staffingUnlock => !selection.has(staffingUnlock.staffingId) && staffingUnlock.unlocked)
		.map(staffingUnlock => staffingUnlock.staffingId);

	const toPayload = (unlocked: boolean, staffingIds: string[]): UpdateStaffingUnlocksPayload[] => {
		if (staffingIds.length === 0) return [];
		if (staffingIds.length === staffingUnlocks.length) return [{ unlocked, scope: "all" }];
		return [{ unlocked, scope: "staffings", staffingIds }];
	};

	return [...toPayload(true, toUnlock), ...toPayload(false, toLock)];
}

/** Nothing is sent on a click in the list: the submit button sends the whole change. */
export function useStaffingUnlockSelection(staffingUnlocks: StaffingUnlock[]) {
	const [selection, setSelection] = useState(() => getInitialSelection(staffingUnlocks));
	const payloads = useMemo(() => getPayloads(staffingUnlocks, selection), [staffingUnlocks, selection]);

	// The list changes after a submit: the selection starts again from the new state.
	useEffect(() => {
		setSelection(getInitialSelection(staffingUnlocks));
	}, [staffingUnlocks]);

	const toggleStaffing = (staffingId: string) => {
		setSelection(previous => {
			const next = new Set(previous);
			if (!next.delete(staffingId)) next.add(staffingId);
			return next;
		});
	};

	const selectAll = () => setSelection(new Set(staffingUnlocks.map(staffingUnlock => staffingUnlock.staffingId)));

	const selectNone = () => setSelection(new Set());

	return { payloads, selectAll, selectNone, selection, toggleStaffing };
}
